/**
 * MANAGEMENT (14) — the cross-lab PLATFORM super-admin console FeatureModule. Five surfaces under
 * /management/*:
 *
 *   /management                    — Tableau de bord (platform KPIs)        [super-admin]
 *   /management/laboratoires       — Laboratoires (directory/provision)     [management.lab.read|onboard]
 *   /management/types-parametres   — Types de paramètres (global catalogue) [management.paramtype.manage]
 *   /management/abonnements        — Abonnements (cross-lab projections)    [management.subscription.manage]
 *   /management/support            — Support (cross-lab helpdesk)           [management.support.manage]
 *
 * AUTHORITY — a SEPARATE plane from the lab cockpit. The server gate is `authenticatePlatform` +
 * `authorizePlatform` (the `est_admin_plateforme` flag IS the authority, re-checked LIVE every request).
 * The client mirrors that:
 *
 *   1. ROUTE GUARD — each route's `element` is wrapped here in <PlatformAdminGuard> (session.ts). The
 *      standard router `wrapFeatureRoutes` ALSO wraps every element in <ProtectedRoute> using the module's
 *      `requiredCapabilities` — which we keep EMPTY so that outer guard is a no-op (any authed user passes
 *      it) and OUR super-admin guard is the real gate. This is the inventory/finance "empty module gate +
 *      per-screen guard" idiom, adapted to the platform plane.
 *
 *   2. NAV GUARD — the AppShell side-nav filters items by `requiredCapabilities` against the live
 *      capability Set. So each nav item carries its section `management.*` capability → the Platform
 *      section appears ONLY for a real operator whose effective set holds them (the cloud install grants
 *      the super-admin the PLATFORM_CAPS set; a lab user's set never contains one). The overview nav item
 *      carries `management.lab.read` as its visibility key (the lightest read cap an operator holds).
 *
 * INTEGRATE GAP (returned, not applied): until `getMe`/`CurrentUser` expose `estAdminPlateforme` AND the
 * single-tenant `/iam/me/capabilities` response includes the PLATFORM_CAPS for a super-admin, the nav uses
 * the capability signal; `<PlatformAdminGuard>` additionally accepts the session flag the moment it lands
 * (it ORs both signals — see session.ts). The exact one-line hookups are in the task return.
 *
 * deployClass parallel: the server module is cloud-only; on a local box the routes 404 and the Overview
 * renders the clean "console not available on this install" notice (api.isManagementAbsent).
 */
import type { FeatureModule } from "@/app";
import { MANAGEMENT_CAPS } from "./caps";
import { registerManagementMessages } from "./messages";
import { registerAnalyticsMessages } from "./analytics.messages";
import { PlatformAdminGuard } from "./components/PlatformAdminGuard";
import { OverviewPage } from "./screens/OverviewPage";
import { OnboardingPage } from "./screens/OnboardingPage";
import { OnboardingReviewPage } from "./screens/OnboardingReviewPage";
import { ParamTypesManagePage } from "./screens/ParamTypesManagePage";
import { SubscriptionsPage } from "./screens/SubscriptionsPage";
import { SupportPage } from "./screens/SupportPage";
// 15 — admin-erp expansion screens. Each self-registers its own i18n at module-eval (idempotent latch);
// the shell adds only the nav labels below so the side-nav resolves before the screen chunk loads.
import { AnalyticsPage } from "./screens/AnalyticsPage";
import { AnalyzersPage } from "./screens/AnalyzersPage";
import { PricingPage } from "./screens/PricingPage";

const SECTION = "nav.section.platform";

const managementModule: FeatureModule = {
  id: "management",
  title: "Console plateforme",
  // EMPTY module gate (see header): the standard <ProtectedRoute> outer wrap becomes a no-op so our
  // <PlatformAdminGuard> per-route wrap (the cross-lab super-admin gate) is the authority.
  requiredCapabilities: [],
  routes: [
    {
      path: "/management",
      element: (
        <PlatformAdminGuard>
          <OverviewPage />
        </PlatformAdminGuard>
      ),
    },
    {
      path: "/management/laboratoires",
      element: (
        <PlatformAdminGuard>
          <OnboardingPage />
        </PlatformAdminGuard>
      ),
    },
    {
      path: "/management/types-parametres",
      element: (
        <PlatformAdminGuard>
          <ParamTypesManagePage />
        </PlatformAdminGuard>
      ),
    },
    {
      path: "/management/abonnements",
      element: (
        <PlatformAdminGuard>
          <SubscriptionsPage />
        </PlatformAdminGuard>
      ),
    },
    {
      path: "/management/support",
      element: (
        <PlatformAdminGuard>
          <SupportPage />
        </PlatformAdminGuard>
      ),
    },
    // 15 — admin-erp expansion routes (each wrapped in <PlatformAdminGuard> exactly like the siblings).
    {
      path: "/management/demandes",
      element: (
        <PlatformAdminGuard>
          <OnboardingReviewPage />
        </PlatformAdminGuard>
      ),
    },
    {
      path: "/management/tarification",
      element: (
        <PlatformAdminGuard>
          <PricingPage />
        </PlatformAdminGuard>
      ),
    },
    {
      path: "/management/analytique",
      element: (
        <PlatformAdminGuard>
          <AnalyticsPage />
        </PlatformAdminGuard>
      ),
    },
    {
      path: "/management/automates",
      element: (
        <PlatformAdminGuard>
          <AnalyzersPage />
        </PlatformAdminGuard>
      ),
    },
  ],
  nav: [
    {
      id: "mgmt-overview",
      path: "/management",
      label: "nav.mgmt.overview",
      icon: "▤",
      // Sorts BELOW every lab section (the platform plane is the last band). The lab-admin section ends
      // at order 100; the platform console opens at 110 so it never elbows a lab surface.
      order: 110,
      section: SECTION,
      requiredCapabilities: [MANAGEMENT_CAPS.labRead],
    },
    {
      id: "mgmt-onboarding",
      path: "/management/laboratoires",
      label: "nav.mgmt.onboarding",
      icon: "⬡",
      order: 111,
      section: SECTION,
      requiredCapabilities: [MANAGEMENT_CAPS.labRead],
    },
    {
      id: "mgmt-paramtypes",
      path: "/management/types-parametres",
      label: "nav.mgmt.paramtypes",
      icon: "≣",
      order: 112,
      section: SECTION,
      requiredCapabilities: [MANAGEMENT_CAPS.paramtypeManage],
    },
    {
      id: "mgmt-subscriptions",
      path: "/management/abonnements",
      label: "nav.mgmt.subscriptions",
      icon: "◆",
      order: 113,
      section: SECTION,
      requiredCapabilities: [MANAGEMENT_CAPS.subscriptionManage],
    },
    {
      id: "mgmt-support",
      path: "/management/support",
      label: "nav.mgmt.support",
      icon: "✉",
      order: 114,
      section: SECTION,
      requiredCapabilities: [MANAGEMENT_CAPS.supportManage],
    },
    // 15 — admin-erp expansion nav items. Fractional orders slot them into the existing platform band
    // without renumbering (never elbowing a sibling): the intake funnel front door sits just under the
    // dashboard; pricing follows subscriptions; analytics + analyzers close the band.
    {
      id: "mgmt-requests",
      path: "/management/demandes",
      label: "nav.mgmt.requests",
      icon: "⬢",
      order: 110.5, // between Tableau de bord (110) and Laboratoires (111) — the funnel front door
      section: SECTION,
      requiredCapabilities: [MANAGEMENT_CAPS.onboardingManage],
    },
    {
      id: "mgmt-pricing",
      path: "/management/tarification",
      label: "nav.mgmt.pricing",
      icon: "₪",
      order: 113.5, // just after Abonnements (113) — the commercial layer that feeds it
      section: SECTION,
      requiredCapabilities: [MANAGEMENT_CAPS.pricingManage],
    },
    {
      id: "mgmt-analytics",
      path: "/management/analytique",
      label: "nav.mgmt.analytics",
      icon: "◫",
      order: 115, // end of the platform band
      section: SECTION,
      requiredCapabilities: [MANAGEMENT_CAPS.analyticsRead],
    },
    {
      id: "mgmt-analyzers",
      path: "/management/automates",
      label: "nav.mgmt.analyzers",
      icon: "⧉",
      order: 116,
      section: SECTION,
      requiredCapabilities: [MANAGEMENT_CAPS.analyzerManage],
    },
  ],
  onRegister() {
    registerManagementMessages();
    // The analytics section has no dedicated screen-level self-register call in its screen module chain the
    // way the others do, so register its i18n here too (idempotent latch — safe alongside the screen's own).
    registerAnalyticsMessages();
  },
};

export default managementModule;
